// Edge-aware, bounded brush selection. No image data leaves the browser.
export function edgeSelection(rgba, width, height, cx, cy, radius) {
  const weights = new Float32Array(width * height);
  const seen = new Uint8Array(width * height);
  const queue = new Int32Array(width * height);
  const sx = Math.max(0, Math.min(width - 1, Math.round(cx)));
  const sy = Math.max(0, Math.min(height - 1, Math.round(cy)));
  const seed = sy * width + sx;
  let head = 0, tail = 1;
  queue[0] = seed;
  seen[seed] = 1;
  while (head < tail) {
    const index = queue[head++], x = index % width, y = Math.floor(index / width);
    const distance = Math.hypot(x - cx, y - cy) / radius;
    if (distance > 1) continue;
    const colorDistance = Math.hypot(...[0, 1, 2].map((c) => rgba[index * 4 + c] - rgba[seed * 4 + c]));
    if (colorDistance > 65) continue;
    weights[index] = Math.min(1, (1 - distance) / 0.25);
    for (const next of [x > 0 ? index - 1 : -1, x + 1 < width ? index + 1 : -1, y > 0 ? index - width : -1, y + 1 < height ? index + width : -1]) {
      if (next < 0 || seen[next]) continue;
      const edge = Math.hypot(...[0, 1, 2].map((c) => rgba[index * 4 + c] - rgba[next * 4 + c]));
      if (edge > 40) continue;
      seen[next] = 1;
      queue[tail++] = next;
    }
  }
  return weights;
}

// Joint bilateral upsampling: use original colour edges to guide the coarse
// person mask without globally dilating the cutout or deleting fine hair.
export function refinePersonConfidence(rgba, width, height, confidence, maskWidth, maskHeight) {
  const result = new Float32Array(width * height);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const offset = (y * width + x) * 4;
    const mx = (x + 0.5) * maskWidth / width - 0.5;
    const my = (y + 0.5) * maskHeight / height - 0.5;
    let total = 0, value = 0;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const nx = Math.max(0, Math.min(maskWidth - 1, Math.round(mx) + dx));
      const ny = Math.max(0, Math.min(maskHeight - 1, Math.round(my) + dy));
      const px = Math.min(width - 1, Math.floor((nx + 0.5) * width / maskWidth));
      const py = Math.min(height - 1, Math.floor((ny + 0.5) * height / maskHeight));
      const other = (py * width + px) * 4;
      let color = 0;
      for (let c = 0; c < 3; c++) color += (rgba[offset + c] - rgba[other + c]) ** 2;
      const weight = Math.exp(-color / 1800 - ((nx - mx) ** 2 + (ny - my) ** 2) / 2);
      total += weight;
      value += weight * confidence[ny * maskWidth + nx];
    }
    result[y * width + x] = total > 1e-12 ? value / total : confidence[Math.max(0, Math.min(maskHeight - 1, Math.round(my))) * maskWidth + Math.max(0, Math.min(maskWidth - 1, Math.round(mx)))];
  }
  return result;
}
